import React from "react";
import { Box, Typography } from "@mui/material";
import { ErrorOutline, GroupOutlined, TimerOutlined, VisibilityOutlined } from "@mui/icons-material";
import { COMPLETION_COLORS } from "utils/backrrTokens";
import { formatCount } from "utils/chartsPalette";
import { PALETTE as P, CARD, NUM, TYPE, SPACE, tileRow } from "./tokens";
import * as S from "./platform";
import { Card, CountUp, Delta, Fill, RankedBarList, BarSparkline, AreaChart, Skeleton, EmptyState } from "./primitives";
import WidgetGate from "../../components/WidgetGate";
import { DragGrid, DragItem, cardGrid, shownCount } from "../../dnd/WidgetGridDnd";

// The startup-platform widgets (profile completion, uploads, active users,
// session time, activity, rounds, profile views, data rooms). They come from a
// second payload — startup-backend's /admin/platform-analytics — so each one
// carries its own loading and error state instead of holding up the page.
//
// Built once and spread into every variant's `blocks`, so A, B and C show the
// same figures; the variant still decides where they sit.

// Widgets that read best across the whole row.
export const PLATFORM_FULL_WIDTH = ["platform_activity", "startup_views", "pitch_uploads"];

const Hero = ({ value, delta, unit, sx }) => (
  <Box display="flex" alignItems="baseline" gap={SPACE.md} flexWrap="wrap">
    {typeof value === "string" ? (
      <Typography component="span" sx={{ ...NUM, fontSize: "1.9rem", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.05, color: P.ink, ...sx }}>
        {value}
      </Typography>
    ) : (
      <CountUp value={value} sx={{ fontSize: "1.9rem", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.05, color: P.ink, ...sx }} />
    )}
    {unit && <Typography component="span" sx={{ fontSize: TYPE.meta, color: P.inkMuted }}>{unit}</Typography>}
    <Delta value={delta} />
  </Box>
);

// Small labelled figures under a hero number. One row on wide cards; four
// figures fold to 2×2 on a narrow card rather than leaving one orphan below 3.
const StatGrid = ({ stats }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: `repeat(${stats.length === 4 ? 2 : stats.length}, minmax(0, 1fr))`,
        sm: `repeat(${stats.length}, minmax(0, 1fr))`,
      },
      gap: SPACE.sm,
    }}
  >
    {stats.map((s) => (
      <Box key={s.label} sx={{ padding: "10px 12px", borderRadius: "12px", background: P.accentSoft, minWidth: 0 }}>
        <Typography sx={{ display: "block", fontSize: TYPE.micro, color: P.inkMuted }}>{s.label}</Typography>
        <Box display="flex" alignItems="baseline" gap={0.75} flexWrap="wrap">
          <Typography sx={{ ...NUM, fontSize: "1.05rem", fontWeight: 600, color: P.ink }}>{s.value ?? "—"}</Typography>
          {s.delta !== undefined && <Delta value={s.delta} />}
        </Box>
      </Box>
    ))}
  </Box>
);

// Horizontal bars for a short list, each scaled to the largest.
const BarRows = ({ rows, color = P.accent, delay = 0.6, format = formatCount }) => {
  const max = rows.reduce((m, r) => Math.max(m, r.count ?? 0), 0);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      {rows.map((r, i) => (
        <Box key={r.key ?? r.label} className="dv-item" style={{ animationDelay: `${delay + i * 0.04}s` }}>
          <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={SPACE.md} sx={{ marginBottom: "6px" }}>
            <Typography sx={{ fontSize: TYPE.label, color: P.ink }}>{r.label}</Typography>
            <Box display="inline-flex" alignItems="baseline" gap={0.75}>
              <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{format(r.count)}</Typography>
              {r.pct != null && <Typography sx={{ ...NUM, fontSize: TYPE.micro, color: P.inkMuted }}>{r.pct}%</Typography>}
            </Box>
          </Box>
          <Fill ratio={max ? (r.count ?? 0) / max : 0} color={r.color ?? color} delay={delay + 0.05 + i * 0.06} />
        </Box>
      ))}
    </Box>
  );
};

const Footnote = ({ children }) =>
  children ? <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{children}</Typography> : null;

const LoadError = () => (
  <EmptyState icon={<ErrorOutline />} title="Couldn't load startup analytics" text="The request failed. Try a different range, or refresh the page." />
);

// One card wrapper that knows the second payload's state.
const PlatformCard = ({ state, height = 180, children, ...card }) => (
  <Card {...card}>
    {state.isLoading ? (
      <Skeleton height={height} />
    ) : state.isError ? (
      <LoadError />
    ) : (
      children
    )}
  </Card>
);

// ---- 1. Profile completion ---------------------------------------------------

// One 100% bar split into the five bands, then a row per band. The rows are the
// legend: each repeats the swatch beside its label and count.
const CompletionBar = ({ buckets, total }) => (
  <Box
    role="img"
    aria-label={buckets.map((b) => `${b.label}: ${b.count}`).join(", ")}
    sx={{ display: "flex", gap: "2px", height: 18, borderRadius: "6px", overflow: "hidden", background: P.track }}
  >
    {buckets.filter((b) => b.count > 0).map((b) => (
      <Box
        key={b.key}
        className="dv-fill"
        title={`${b.label}: ${formatCount(b.count)} (${b.pct ?? 0}%)`}
        sx={{ flex: `${b.count} 0 0`, minWidth: "4px", background: COMPLETION_COLORS[b.key] }}
      />
    ))}
    {total === 0 && <Box sx={{ flex: 1 }} />}
  </Box>
);

const ProfileCompletion = ({ platform, state }) => {
  const pc = S.profileCompletion(platform);
  return (
    <PlatformCard
      state={state}
      title="Profile Completion"
      subtitle={pc.subtitle}
      hint="Startups created in the selected range, grouped by how complete their profile is now, scored with the weights set under Profile Score. Completion is not recorded over time, so this is today's score for those startups."
      delay={0.3}
    >
      <CompletionBar buckets={pc.buckets} total={pc.total} />
      <BarRows rows={pc.buckets.map((b) => ({ ...b, color: COMPLETION_COLORS[b.key] }))} />
      <Footnote>{pc.footnote}</Footnote>
    </PlatformCard>
  );
};

// ---- 2 & 3. Pitch deck and video uploads -------------------------------------

const PitchUploads = ({ platform, state, view, onItemOrderChange }) => {
  const cards = S.uploads(platform);
  return (
    <PlatformCard
      state={state}
      title="Pitch Uploads"
      subtitle="Startups with each asset on their profile, and uploads in this range"
      hint="The large figure is how many startup profiles hold the asset today. The badge and bars count uploads inside the selected range; a replaced file counts as a new upload. Files uploaded before upload times were recorded are in the total only."
      delay={0.34}
    >
      <Box sx={tileRow(220, SPACE.md, shownCount(view, "pitch_uploads", cards))}>
        <DragGrid {...cardGrid(view, "pitch_uploads", cards, onItemOrderChange)}>
          {view.sortItems("pitch_uploads", cards).map((c, i) => (
            <WidgetGate key={c.key} fill show={view.item("pitch_uploads", c.key)}>
              <DragItem id={c.key} label={c.label} radius="14px">
                <Box className="dv-item" style={{ animationDelay: `${0.5 + i * 0.05}s` }} sx={{ borderRadius: "12px", background: P.surface, border: `1px solid ${P.line}`, padding: "16px", display: "flex", flexDirection: "column", gap: SPACE.sm }}>
                  <Typography sx={{ fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>{c.label}</Typography>
                  <Box display="flex" alignItems="flex-end" justifyContent="space-between" gap={SPACE.md}>
                    <Box>
                      <CountUp value={c.total} sx={{ fontSize: "1.6rem", fontWeight: 600, color: P.ink }} />
                      <Box display="flex" alignItems="center" gap={0.75} sx={{ marginTop: "2px" }}>
                        <Typography sx={{ ...NUM, fontSize: TYPE.micro, color: P.inkSoft }}>{formatCount(c.inRange ?? 0)} in range</Typography>
                        <Delta value={c.delta} />
                      </Box>
                    </Box>
                    <BarSparkline values={c.series} width={96} height={32} delay={0.6 + i * 0.05} />
                  </Box>
                  <Fill ratio={(c.coverage ?? 0) / 100} color={P.accent} delay={0.7 + i * 0.05} />
                  <Footnote>{c.footnote}</Footnote>
                </Box>
              </DragItem>
            </WidgetGate>
          ))}
        </DragGrid>
      </Box>
    </PlatformCard>
  );
};

// ---- 4. Active users -----------------------------------------------------------

const ActiveUsers = ({ platform, state }) => {
  const a = S.activeUsers(platform);
  return (
    <PlatformCard
      state={state}
      title="Active Users"
      subtitle={a.subtitle}
      hint="Distinct founders who opened the startup app in the range, one count per founder however often they came back. The daily line counts each founder once per day."
      delay={0.38}
    >
      {a.tracked ? (
        <>
          <Hero value={a.value} delta={a.delta} unit="founders" />
          <AreaChart values={a.series} color={P.series[0]} label={`Active founders per day, ${a.series.length} days`} delay={0.55} gid="pf-active" />
          <StatGrid stats={a.stats} />
        </>
      ) : (
        <EmptyState
          icon={<GroupOutlined />}
          title="No app sessions yet"
          text={`Active founders appear once they open the updated startup app.${a.lastLogin != null ? ` Meanwhile, ${formatCount(a.lastLogin)} founders last logged in during this range.` : ""}`}
        />
      )}
    </PlatformCard>
  );
};

// ---- 5. Average session time ---------------------------------------------------

const SessionTime = ({ platform, state }) => {
  const s = S.sessionTime(platform);
  return (
    <PlatformCard
      state={state}
      title="Average Session Time"
      subtitle="Time founders spend in the startup app per visit"
      hint="Only time with the app's tab visible counts. A visit that has not reported any time yet is left out of the average, and a return after 30 idle minutes is a new visit."
      delay={0.42}
    >
      {s.measured ? (
        <>
          <Hero value={s.formatted} delta={s.delta} unit="per visit" />
          <BarRows rows={s.distribution} color={P.pipeline.interested} delay={0.55} />
          <StatGrid stats={s.stats} />
        </>
      ) : (
        <EmptyState icon={<TimerOutlined />} title="No sessions in this range" text="Average time per visit appears once founders spend time in the startup app." />
      )}
    </PlatformCard>
  );
};

// ---- 6. Activity -----------------------------------------------------------------

const PlatformActivity = ({ platform, state, view, onItemOrderChange }) => {
  const cards = S.activity(platform);
  return (
    <Box className="dv-fade" style={{ animationDelay: ".46s" }} component="section" aria-label="Activity" sx={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={SPACE.sm}>
        <Typography component="h2" sx={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: P.ink }}>Activity</Typography>
        <Typography sx={{ fontSize: TYPE.meta, color: P.inkMuted }}>What founders and investors did in this range</Typography>
      </Box>
      {state.isLoading ? (
        <Skeleton height={120} />
      ) : state.isError ? (
        <LoadError />
      ) : (
        <Box sx={tileRow(200, SPACE.md, shownCount(view, "platform_activity", cards))}>
          <DragGrid {...cardGrid(view, "platform_activity", cards, onItemOrderChange)}>
            {view.sortItems("platform_activity", cards).map((c, i) => (
              <WidgetGate key={c.key} fill show={view.item("platform_activity", c.key)}>
                <DragItem id={c.key} label={c.label} radius="14px">
                  <Box className="dv-card" style={{ animationDelay: `${0.5 + i * 0.05}s` }} sx={{ ...CARD, display: "flex", flexDirection: "column", gap: SPACE.sm }}>
                    <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft, fontWeight: 500 }}>{c.label}</Typography>
                    <Box display="flex" alignItems="flex-end" justifyContent="space-between" gap={SPACE.sm}>
                      <Box display="flex" alignItems="baseline" gap={SPACE.sm}>
                        <CountUp value={c.value} sx={{ fontSize: "1.5rem", fontWeight: 600, color: P.ink }} />
                        <Delta value={c.delta} />
                      </Box>
                      <BarSparkline values={c.series} width={80} height={28} delay={0.6 + i * 0.04} />
                    </Box>
                    <Footnote>{c.previous != null ? `${formatCount(c.previous)} in the previous period` : ""}</Footnote>
                  </Box>
                </DragItem>
              </WidgetGate>
            ))}
          </DragGrid>
        </Box>
      )}
    </Box>
  );
};

// ---- 9. Rounds created ---------------------------------------------------------

const Rounds = ({ platform, state }) => {
  const r = S.rounds(platform);
  return (
    <PlatformCard
      state={state}
      title="Rounds Created"
      subtitle="Funding rounds startups set up in this range"
      hint="Rounds created in the range, against every round on the platform. 'Marked as raised' are rounds a startup recorded as already closed."
      delay={0.5}
    >
      <Hero value={r.value} delta={r.delta} unit="rounds" />
      <AreaChart values={r.series} color={P.series[1]} label={`Rounds created per day, total ${r.value ?? 0}`} delay={0.6} gid="pf-rounds" />
      <StatGrid stats={r.stats} />
    </PlatformCard>
  );
};

// ---- 10. Startup profile views -------------------------------------------------

const StartupViews = ({ platform, state }) => {
  const v = S.startupViews(platform);
  return (
    <PlatformCard
      state={state}
      height={260}
      title="Startup Profile Views"
      subtitle="Investors viewing startup profiles, counted the way founders see it"
      hint="One view is one investor on one startup's profile on one day, the same rule as the founder's Profile Views screen, summed across every startup. Investors whose account no longer exists are left out."
      delay={0.54}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: SPACE.xl }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.lg, minWidth: 0 }}>
          <Hero value={v.value} delta={v.delta} unit="profile views" />
          <AreaChart values={v.series} color={P.series[0]} label={`Profile views per day, total ${v.value ?? 0}`} delay={0.65} gid="pf-views" />
          <StatGrid stats={v.stats} />
          <Box>
            <Typography component="h3" sx={{ margin: "0 0 8px", fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>Other interactions</Typography>
            <BarRows rows={v.interactions} color={P.pipeline.interested} delay={0.7} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.lg, minWidth: 0 }}>
          <Box>
            <Typography component="h3" sx={{ margin: "0 0 8px", fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>Most viewed startups</Typography>
            {v.top.length ? (
              <RankedBarList rows={v.top} delay={0.8} />
            ) : (
              <EmptyState minHeight={96} icon={<VisibilityOutlined />} title="No profile views in this range" text="Startups investors view most will be ranked here." />
            )}
          </Box>
        </Box>
      </Box>
    </PlatformCard>
  );
};

// ---- 11. Data rooms --------------------------------------------------------------

const DataRooms = ({ platform, state }) => {
  const d = S.dataRooms(platform);
  const rooms = d.totals.data_rooms ?? 0;
  return (
    <PlatformCard
      state={state}
      title="Data Rooms"
      subtitle="Rooms created and how investors use them"
      hint="Rooms created in the range, all-time room settings, and investor views of rooms and their documents in the range (one view per investor, room and day)."
      delay={0.58}
    >
      <Hero value={d.value} delta={d.delta} unit="created" />
      <StatGrid stats={d.engagement} />
      <Box>
        <Typography component="h3" sx={{ margin: "0 0 8px", fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>Who can open them</Typography>
        <BarRows rows={d.access.map((a) => ({ ...a, pct: rooms ? Math.round((a.count / rooms) * 1000) / 10 : null }))} color={P.pipeline.interested} delay={0.7} />
      </Box>
      <StatGrid stats={d.stats} />
      <Footnote>
        {d.totals.empty_rooms ? `${formatCount(d.totals.empty_rooms)} rooms have no documents yet · ` : ""}
        {formatCount(d.totals.shared_with_investors ?? 0)} shared with named investors
      </Footnote>
    </PlatformCard>
  );
};

/**
 * The platform widgets keyed by their catalog key, ready to spread into a
 * variant's `blocks`. `state` is { isLoading, isError } for the platform call.
 */
export const platformBlocks = ({ platform, state, view, onItemOrderChange }) => {
  const props = { platform, state, view, onItemOrderChange };
  return {
    profile_completion: <ProfileCompletion {...props} />,
    pitch_uploads: <PitchUploads {...props} />,
    active_users: <ActiveUsers {...props} />,
    session_time: <SessionTime {...props} />,
    platform_activity: <PlatformActivity {...props} />,
    rounds: <Rounds {...props} />,
    startup_views: <StartupViews {...props} />,
    data_rooms: <DataRooms {...props} />,
  };
};
